import React, { useCallback } from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  CategoryScale,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  ButtonGroup,
  Typography,
  useMediaQuery
} from '@material-ui/core';
import { useState } from 'react';
import clsx from 'clsx';
import SelectInput from '../Inputs/SelectInput';

ChartJS.register(
  CategoryScale,
  BarElement,
  Title,
  Tooltip,
  LinearScale,
  Legend
);

export const BarChartTypes = {
  Industrial: 'industrial',
  NonIndustrial: 'non-industrial'
};

/**
 *
 * @param {title} title  is the top title of the graphic
 * @param {features} features  are the references array to the bars that are graphic !Important (features.length === data.length)
 * @param {data} data  is the data array to be graphed, data model [...{ label string, data []int, backgroundColor  string}]
 * @param {height} height  this is optional, it changes the size in height of the graph
 */
const BarChart = ({
  title = 'Graph',
  type = BarChartTypes.NonIndustrial,
  features = [],
  data = [],
  height,
  defaultDataset = 1
}) => {
  const classes = useStyles();
  const [datasetIndex, setDatasetIndex] = useState(defaultDataset);
  const smallScreen = useMediaQuery(theme =>
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  const graphOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'top',
        onHover: (event, item) => {
          event.native.target.style.cursor = item.hidden
            ? 'pointer'
            : 'default';
        },
        onLeave: event => {
          event.native.target.style.cursor = 'default';
        }
      },
      title: {
        display: true,
        text: ''
      },
      datalabels: {
        display: 'auto',
        formatter: (value, context) => {
          if (type === BarChartTypes.Industrial) {
            return Math.round(value);
          }

          return `${Math.round(value)} ${context.dataset.unit ||
            context.dataset.label}`;
        },
        anchor: 'end',
        offset: -20,
        align: 'start'
      }
    }
  };

  const dataBar = {
    labels: features,
    datasets: [...data]
  };

  const onClick = useCallback(
    index => {
      setDatasetIndex(data.length > 2 ? index : (index + 1) % 2);
      dataBar.datasets.forEach((dataset, dataSetIndex) => {
        dataset.hidden = index !== dataSetIndex;
      });
    },
    [dataBar.datasets, data.length]
  );

  const renderSelector = () => {
    if (smallScreen && type === BarChartTypes.Industrial) {
      return (
        <SelectInput
          id="consumption-selector"
          label="Concepto"
          className={classes.sizeButton}
          value={datasetIndex}
          onChange={event => onClick(Number(event.target.value))}
          options={data.map((info, idx) => ({
            label: info.label,
            value: idx
          }))}
        />
      );
    }

    if (data.length < 2) {
      const info = data[0];
      return (
        <Button
          id={`consumption-${info.label}`}
          size="small"
          disabled
          className={clsx(classes.sizeButton, info.buttonStyle)}
        >
          {info.label} {info.unit ? `(${info.unit})` : ''}
        </Button>
      );
    }

    return (
      <ButtonGroup disableElevation color="primary">
        {data.map(info => (
          <Button
            key={`consumption-${info.index}`}
            id={`consumption-${info.label}`}
            size="small"
            disabled={
              data.length > 2
                ? info.index === datasetIndex
                : info.index !== datasetIndex
            }
            onClick={() => onClick(info.index)}
            className={clsx(classes.sizeButton, info.buttonStyle)}
          >
            {info.label} {info.unit ? `(${info.unit})` : ''}
          </Button>
        ))}
      </ButtonGroup>
    );
  };

  if (features.length === 0 || data.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className={classes.legend}>
        <Typography className={classes.title}>{title}</Typography>
        {renderSelector()}
      </div>
      <Bar
        plugins={[ChartDataLabels]}
        options={graphOptions}
        height={height}
        data={dataBar}
      />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  legend: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'inline-grid',
    paddingTop: theme.spacing()
  },
  title: {
    textAlign: 'center',
    padding: theme.spacing(),
    fontWeight: '600',
    color: theme.palette.text.greyDark
  },
  sizeButton: {
    color: theme.palette.common.white,
    textTransform: 'none',
    minWidth: 55
  }
}));

export default BarChart;
